import React, { useEffect, useState, useContext, useCallback} from 'react';
import { X, Trash2 } from 'lucide-react';
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from '../models/Image';
import { SettingsContext } from '../frames/Main';
import { useToast } from './ToastContext';

type ImageSelectorProps = {
    value: string,
    placeholder?: string,
    buttonText?: string,
    onImageChange: (src: string) => void
};

const ImageSelector = ({ value, placeholder = "Select an image", buttonText = "Select / Update", onImageChange }: ImageSelectorProps) => {

    const [images, setImages] = useState<Image[]>([]);
    const [open, setOpen] = useState<boolean>(false);
    const { IMAGE_URL } = useContext(SettingsContext);

    const { setIsToastOpen, setToastMessage, setToastTitle } = useToast();

    const onImageClick = function (e: React.MouseEvent) {
        const img = e.target as HTMLImageElement;
        var imgSrc = img.getAttribute('src');
        onImageChange(imgSrc!);
        setOpen(false);
    }

    const onDeleteImageClick = async function (image: Image) {
        const imgUrl = IMAGE_URL;
        const requestBody = {
            fileName: image.fileName
        };

        try {
            const response = await fetch(imgUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                method: "DELETE",
                body: JSON.stringify(requestBody)
            });

            if (response.ok) {
                setToastTitle("Success");
                setToastMessage("Image " + image.fileName + " deleted.");
                await loadImagePaths();
            } else {
                setToastTitle("Error");
                setToastMessage((await response.json()).message);
            }
        } catch {
            setToastTitle("Error");
            setToastMessage("Image deletion unsuccessful. Please try again.")
        }
        setIsToastOpen(true);
    }

    const loadImagePaths = useCallback(async () => {
        const imgUrl = IMAGE_URL;

        try {
            let loadedPaths = await fetch(imgUrl, {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            }).then((response) => {
                return response.json();
            });
            setImages(loadedPaths);
        } catch { }
    }, [IMAGE_URL]);

    useEffect(() => {
        (async function () {
           await loadImagePaths();
        })();
    }, [loadImagePaths]);

    return (
        <div className='flex flex-wrap'>
            <input
                className="appearance-none bg-gray-200 flex-1 me-3 text-gray-700 border border-gray-200 rounded py-2 px-4 leading-tight focus:border-gray-400"
                value={value}
                placeholder={placeholder}
                readOnly />

            <Dialog.Root open={open} onOpenChange={setOpen}>
                <Dialog.Trigger asChild>
                    <button
                        type="button"
                        className="text-white bg-primary-blue hover:bg-blue-800 focus:outline-none font-medium rounded-lg text-sm px-2.5 py-1.5 text-center inline-flex items-center">{buttonText}</button>
                </Dialog.Trigger>
                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 bg-blackA6 data-[state=open]:animate-overlayShow" />
                    <Dialog.Content className="overflow-y-auto fixed left-1/2 top-1/2 max-h-[85vh] w-[90vw] max-w-[850px] -translate-x-1/2 -translate-y-1/2 rounded-md bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
                        <Dialog.Title className='text-xl font-semibold text-gray-900'>
                            Select an image
                        </Dialog.Title>
                        <Dialog.Description />

                        <div className="p-4 md:p-5 space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3">
                                {
                                    images.map((item, index) => (
                                        <div key={index} className='relative'>
                                            <img
                                                className="object-cover object-center w-full h-40 max-w-full rounded-lg pointer-cursor"
                                                src={item.src}
                                                alt={item.fileName}
                                                onClick={onImageClick} />
                                            <button type="button" 
                                                onClick={async () => {await onDeleteImageClick(item)}}
                                                className="absolute top-0 end-0 z-30 flex justify-center h-full px-4 cursor-pointer" >
                                                <span className="inline-flex items-center justify-center w-10 h-10 text-white rounded-full bg-red-600 shadow hover:bg-red-400">
                                                    <Trash2 />
                                                </span>
                                            </button>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        <Dialog.Close asChild>
                            <div className="flex items-center p-4 md:p-5 border-t border-gray-200 rounded-b">
                                <button
                                    type="button"
                                    className="py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10">Close</button>
                                <button
                                    className="absolute right-2.5 top-5 inline-flex size-[25px] appearance-none items-center justify-center focus:outline-none text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto"
                                    aria-label="Close">
                                    <X />
                                </button>
                            </div>
                        </Dialog.Close>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </div>
    )
}

export default ImageSelector;

